import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixContactsV4Attachment from './contacts-v4-attachment-attachments.http.js';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTDocumentToSDKDocument } from '@wix/sdk-runtime/transformations/document';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

export interface ContactAttachment extends ContactAttachmentMediaOneOf {
  /** Image details when the attachment type is `IMAGE`. */
  image?: string;
  /** Document details when the attachment type is `OTHER`. */
  document?: string;
  /**
   * Attachment ID.
   * @readonly
   * @format GUID
   */
  _id?: string;
  /**
   * Details of the preview image reduced size when the attachment is of type `IMAGE`.
   * @readonly
   */
  previewImage?: string;
  /**
   * Name of the attachment file.
   * @readonly
   */
  fileName?: string;
  /**
   * Mime type of the attachment. See [supported mime types](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/attachments/attachment-v4/supported-mime-types).
   * @readonly
   */
  mimeType?: string;
  /**
   * Type of the attachment.
   * @readonly
   */
  attachmentType?: AttachmentTypeWithLiterals;
}

/** @oneof */
export interface ContactAttachmentMediaOneOf {
  /** Image details when the attachment type is `IMAGE`. */
  image?: string;
  /** Document details when the attachment type is `OTHER`. */
  document?: string;
}

export enum AttachmentType {
  /** Unknown attachment type. */
  UNKNOWN = 'UNKNOWN',
  /** Attachment is an image file. */
  IMAGE = 'IMAGE',
  /** Attachment is a document or any non-image file type. */
  OTHER = 'OTHER',
}

/** @enumType */
export type AttachmentTypeWithLiterals =
  | AttachmentType
  | 'UNKNOWN'
  | 'IMAGE'
  | 'OTHER';

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface CrmAttachmentUploadedEvent {
  attachment?: CrmAttachment;
  /** @format GUID */
  uploadJobId?: string;
}

export interface CrmAttachment {
  /**
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  revision?: string | null;
  /** @readonly */
  _createdDate?: Date | null;
  /** @readonly */
  _updatedDate?: Date | null;
  /** @readonly */
  source?: AttachmentSource;
  metadata?: AttachmentMetadata;
  media?: AttachmentMedia;
  /**
   * Inferred by the media type
   * @readonly
   */
  attachmentType?: CrmAttachmentAttachmentTypeWithLiterals;
}

export interface AttachmentSource {
  appId?: string;
}

export interface AttachmentMetadata {
  /**
   * Entity type e.g: INVOICES
   * @minLength 2
   * @maxLength 50
   */
  entityType?: string | null;
  /**
   * Id of the entity, e.g: invoice id
   * @minLength 1
   * @maxLength 50
   */
  entityId?: string | null;
  /** @format GUID */
  contactId?: string | null;
  /**
   * @minLength 1
   * @maxLength 50
   */
  externalId?: string | null;
}

export interface AttachmentMedia extends AttachmentMediaMediaOneOf {
  image?: string;
  document?: string;
  /** @readonly */
  previewImage?: string;
  /** @minLength 1 */
  fileName?: string;
  /**
   * @minLength 1
   * @maxLength 100
   */
  mimeType?: string;
}

/** @oneof */
export interface AttachmentMediaMediaOneOf {
  image?: string;
  document?: string;
}

export enum CrmAttachmentAttachmentType {
  UNKNOWN = 'UNKNOWN',
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
}

/** @enumType */
export type CrmAttachmentAttachmentTypeWithLiterals =
  | CrmAttachmentAttachmentType
  | 'UNKNOWN'
  | 'DOCUMENT'
  | 'IMAGE';

export interface CreateDemoAttachmentRequest {
  /**
   * The contact id whose demo attachment is being created
   * @format GUID
   */
  contactId?: string;
  /** The type of the demo contact */
  demoContactType?: DemoContactTypeWithLiterals;
}

export enum DemoContactType {
  KAT = 'KAT',
  DAN = 'DAN',
}

/** @enumType */
export type DemoContactTypeWithLiterals = DemoContactType | 'KAT' | 'DAN';

export interface CreateDemoAttachmentResponse {
  /** The created attachment */
  attachment?: ContactAttachment;
}

export interface GenerateAttachmentUploadUrlRequest {
  /**
   * ID of the contact for whom the attachment is being uploaded.
   * @format GUID
   */
  contactId: string;
  /**
   * File name of the attachment including the extension, for example, `contact-cv.pdf`.
   * @minLength 1
   * @maxLength 260
   */
  fileName: string;
  /**
   * Mime type of the attachment file, for example, `application/pdf`.
   * See [supported mime types](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/attachments/attachment-v4/supported-mime-types).
   * @minLength 1
   * @maxLength 255
   */
  mimeType: string;
}

export interface GenerateAttachmentUploadUrlResponse {
  /**
   * The URL for uploading a file as an attachment to the contact.
   * @format WEB_URL
   */
  uploadUrl?: string;
}

export interface ListAttachmentsRequest {
  /**
   * Contact ID for the attachments to be listed.
   * @format GUID
   */
  contactId: string;
  /** Allow paginating, default: limit = 100 and offset = 0 */
  paging?: Paging;
}

export interface Paging {
  /**
   * Number of items to return.
   *
   * Default: `100`
   * Max: `100`
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListAttachmentsResponse {
  /** List of attachments for the specified contact. */
  attachments?: ContactAttachment[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Whether the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface DeleteAttachmentRequest {
  /**
   * Contact ID for the attachment to delete.
   * @format GUID
   */
  contactId: string;
  /**
   * Attachment ID to delete.
   * @format GUID
   */
  attachmentId: string;
}

export interface DeleteAttachmentResponse {}

export interface GetAttachmentRequest {
  /**
   * Contact ID for the attachment to retrieve.
   * @format GUID
   */
  contactId: string;
  /**
   * Attachment ID to retrieve.
   * @format GUID
   */
  attachmentId: string;
}

export interface GetAttachmentResponse {
  /** The requested attachment. */
  attachment?: ContactAttachment;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface AttachmentCreatedEnvelope {
  entity: ContactAttachment;
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Contact Attachments
 * @permissionScopeId SCOPE.DC-CONTACTS.MANAGE-CONTACT-ATTACHMENTS
 * @permissionScope Manage Members and Contacts - all permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.MANAGE-MEMBERS-CONTACTS
 * @permissionId CONTACTS_ATTACHMENTS.VIEW
 * @webhook
 * @eventType wix.contacts.v4.attachment_created
 * @slug created
 */
export declare function onAttachmentCreated(
  handler: (event: AttachmentCreatedEnvelope) => void | Promise<void>
): void;

export interface AttachmentDeletedEnvelope {
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Contact Attachments
 * @permissionScopeId SCOPE.DC-CONTACTS.MANAGE-CONTACT-ATTACHMENTS
 * @permissionScope Manage Members and Contacts - all permissions
 * @permissionScopeId SCOPE.DC-CONTACTS-MEGA.MANAGE-MEMBERS-CONTACTS
 * @permissionId CONTACTS_ATTACHMENTS.VIEW
 * @webhook
 * @eventType wix.contacts.v4.attachment_deleted
 * @slug deleted
 */
export declare function onAttachmentDeleted(
  handler: (event: AttachmentDeletedEnvelope) => void | Promise<void>
): void;

/**
 * Generates an upload URL for uploading a file as an attachment to a specified contact.
 *
 * To learn how to use the generated upload URL in the response to upload an attachment file,
 * see the [Upload API](https://dev.wix.com/docs/rest/assets/media/media-manager/files/upload-api).
 * @param contactId - ID of the contact for whom the attachment is being uploaded.
 * @param fileName - File name of the attachment including the extension, for example, `contact-cv.pdf`.
 * @public
 * @requiredField contactId
 * @requiredField fileName
 * @requiredField options
 * @requiredField options.mimeType
 * @permissionId CONTACTS_ATTACHMENTS.MODIFY
 * @applicableIdentity APP
 * @fqn com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.GenerateAttachmentUploadUrl
 */
export async function generateAttachmentUploadUrl(
  contactId: string,
  fileName: string,
  options: NonNullablePaths<GenerateAttachmentUploadUrlOptions, `mimeType`, 2>
): Promise<
  NonNullablePaths<GenerateAttachmentUploadUrlResponse, `uploadUrl`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    contactId: contactId,
    fileName: fileName,
    mimeType: options?.mimeType,
  });

  const reqOpts =
    ambassadorWixContactsV4Attachment.generateAttachmentUploadUrl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          contactId: '$[0]',
          fileName: '$[1]',
          mimeType: '$[2].mimeType',
        },
        singleArgumentUnchanged: false,
      },
      ['contactId', 'fileName', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GenerateAttachmentUploadUrlOptions {
  /**
   * Mime type of the attachment file, for example, `application/pdf`.
   * See [supported mime types](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/attachments/attachment-v4/supported-mime-types).
   * @minLength 1
   * @maxLength 255
   */
  mimeType: string;
}

/**
 * Retrieves a list of attachments associated with a specified contact.
 * @param contactId - Contact ID for the attachments to be listed.
 * @public
 * @requiredField contactId
 * @permissionId CONTACTS_ATTACHMENTS.VIEW
 * @applicableIdentity APP
 * @fqn com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.ListAttachments
 */
export async function listAttachments(
  contactId: string,
  options?: ListAttachmentsOptions
): Promise<
  NonNullablePaths<
    ListAttachmentsResponse,
    | `attachments`
    | `attachments.${number}._id`
    | `attachments.${number}.fileName`
    | `attachments.${number}.mimeType`
    | `attachments.${number}.attachmentType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    contactId: contactId,
    paging: options?.paging,
  });

  const reqOpts = ambassadorWixContactsV4Attachment.listAttachments(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'attachments.image' },
            { path: 'attachments.previewImage' },
          ],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'attachments.document' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { contactId: '$[0]', paging: '$[1].paging' },
        singleArgumentUnchanged: false,
      },
      ['contactId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListAttachmentsOptions {
  /** Allow paginating, default: limit = 100 and offset = 0 */
  paging?: Paging;
}

/**
 * Deletes the specified attachment from the specified contact.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.attachmentId
 * @requiredField identifiers.contactId
 * @permissionId CONTACTS_ATTACHMENTS.MODIFY
 * @applicableIdentity APP
 * @fqn com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.DeleteAttachment
 */
export async function deleteAttachment(
  identifiers: NonNullablePaths<
    DeleteAttachmentIdentifiers,
    `attachmentId` | `contactId`,
    2
  >
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    contactId: identifiers?.contactId,
    attachmentId: identifiers?.attachmentId,
  });

  const reqOpts = ambassadorWixContactsV4Attachment.deleteAttachment(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          contactId: '$[0].contactId',
          attachmentId: '$[0].attachmentId',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteAttachmentIdentifiers {
  /**
   * Contact ID for the attachment to delete.
   * @format GUID
   */
  contactId: string;
  /**
   * Attachment ID to delete.
   * @format GUID
   */
  attachmentId: string;
}

/**
 * Retrieves the specified attachment for the specified contact.
 * @public
 * @requiredField identifiers
 * @requiredField identifiers.attachmentId
 * @requiredField identifiers.contactId
 * @permissionId CONTACTS_ATTACHMENTS.VIEW
 * @applicableIdentity APP
 * @returns The requested attachment.
 * @fqn com.wixpress.contacts.attachments.v4.ContactAttachmentsServiceV4.GetAttachment
 */
export async function getAttachment(
  identifiers: NonNullablePaths<
    GetAttachmentIdentifiers,
    `attachmentId` | `contactId`,
    2
  >
): Promise<
  NonNullablePaths<
    ContactAttachment,
    `_id` | `fileName` | `mimeType` | `attachmentType`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    contactId: identifiers?.contactId,
    attachmentId: identifiers?.attachmentId,
  });

  const reqOpts = ambassadorWixContactsV4Attachment.getAttachment(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'attachment.image' },
            { path: 'attachment.previewImage' },
          ],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'attachment.document' }],
        },
      ])
    )?.attachment!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          contactId: '$[0].contactId',
          attachmentId: '$[0].attachmentId',
        },
        singleArgumentUnchanged: false,
      },
      ['identifiers']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetAttachmentIdentifiers {
  /**
   * Contact ID for the attachment to retrieve.
   * @format GUID
   */
  contactId: string;
  /**
   * Attachment ID to retrieve.
   * @format GUID
   */
  attachmentId: string;
}
